// node modules
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

// Material-UI and other components
import withStyles from 'isomorphic-style-loader/withStyles';
import Button from '@material-ui/core/Button';
import Icon from 'components/Icon';
import UserRecipeFeedback from 'components/UserRecipeFeedback';
import AddToCollectionButton from 'components/AddToCollectionButton';
import ContentOptions from 'components/ContentOptions';
import FavouriteButton from 'components/FavouriteButton';
import VoiceAssistantLink from 'components/VoiceAssistantLink';
import ToastContents from 'components/ToastContents';

// Actions
import { closeTOCBeforePrinting, openTableOfContents } from 'actions/tableOfContents';
import { pinPage, unpinPage } from 'actions/pinnedPages';

// Services
import { isIosApp } from 'services/environment';
import history from 'services/history';
import route from 'services/route';

// Constants
import { CONTENT_TYPES, COLOURS } from 'constants/index';

// Styles
import CssGeneralActions from './GeneralActions.scss';

class GeneralActions extends Component {
  static propTypes = {
    canPrint: PropTypes.bool,
    canPinThePage: PropTypes.bool,
    loading: PropTypes.bool,
    contentId: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    hash: PropTypes.string,
    canonicalUrl: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
    baseUrl: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    isLoggedIn: PropTypes.bool,
    previewOnly: PropTypes.bool,
    nativeIOSPrinting: PropTypes.bool,
    shareImage: PropTypes.string,
    voiceLinks: PropTypes.arrayOf(
      PropTypes.shape({ provider: PropTypes.string, url: PropTypes.string }),
    ),
    recipe: PropTypes.shape({}),
    enableVoice: PropTypes.bool,
    openTableOfContents: PropTypes.func,
    closeTOCBeforePrinting: PropTypes.func,
    pinPage: PropTypes.func,
    unpinPage: PropTypes.func,
    tableOfContents: PropTypes.shape({
      isOpen: PropTypes.bool,
      isPrint: PropTypes.bool,
    }),
    pinnedPages: PropTypes.objectOf(PropTypes.bool),
  };

  static defaultProps = {
    canPrint: true,
    canPinThePage: false,
    loading: false,
    hash: '',
    previewOnly: false,
    enableVoice: false,
    nativeIOSPrinting: false,
    isLoggedIn: false,
    shareImage: null,
    voiceLinks: [],
    openTableOfContents: () => {},
    closeTOCBeforePrinting: () => {},
    pinPage: () => {},
    unpinPage: () => {},
    recipe: {},
    tableOfContents: {
      isOpen: false,
      isPrint: false,
    },
    pinnedPages: {},
  };

  constructor(props) {
    super(props);

    this.bound = {
      handlePrint: this.handlePrint.bind(this),
      handleBeforePrint: this.handleBeforePrint.bind(this),
      handleAfterPrint: this.handleAfterPrint.bind(this),
      handleCordovaPrint: this.handleCordovaPrint.bind(this),
      handlePinClick: this.handlePinClick.bind(this),
    };
  }

  handleBeforePrint = () => {
    const { tableOfContents, closeTOCBeforePrinting: closeTOC } = this.props;
    if (tableOfContents.isOpen) {
      closeTOC(true);
    }
  };

  handleAfterPrint = () => {
    const { tableOfContents, openTableOfContents: openTOC } = this.props;
    if (!tableOfContents.isOpen && tableOfContents.isPrint) {
      openTOC();
    }
  };

  handleCordovaPrint = () => {
    const { baseUrl, pathname, nativeIOSPrinting } = this.props;
    const isIos = isIosApp();

    if (!isIos || (isIos && nativeIOSPrinting)) {
      window.cordova.plugins.printer.print(isIos ? undefined : document.documentElement.innerHTML);
    } else {
      const openBrowser = () => {
        window.open(encodeURI(`${baseUrl}/w${pathname}`), '_system');
      };

      toast.info(
        <ToastContents>
          <div>
            To print this recipe,{' '}
            <button onClick={openBrowser} className="toast_msg_url">
              view it on the ckbk website
            </button>
          </div>
        </ToastContents>,
        {
          position: 'bottom-center',
          autoClose: 8000,
          hideProgressBar: true,
        },
      );
    }
  };

  handlePrint = () => {
    const { canPrint, isLoggedIn, pathname } = this.props;

    if (!canPrint && !isLoggedIn) {
      history.push('/login', { returnTo: pathname });
      return;
    }

    if (!canPrint) {
      toast.error('Print is not allowed');
      return;
    }

    if (typeof window !== 'undefined') {
      if (window.cordova && window.cordova.plugins && window.cordova.plugins.printer) {
        this.bound.handleCordovaPrint();
      } else {
        this.bound.handleBeforePrint();
        setTimeout(() => {
          window.print();
          this.bound.handleAfterPrint();
        }, 300);
      }
    }
  };

  handlePinClick() {
    const {
      pinPage: dispatchPinPage,
      unpinPage: dispatchUnpinPage,
      pinnedPages,
      recipe,
    } = this.props;

    const pathname = route.content(recipe.id, recipe.title);
    const savedPathname = Object.keys(pinnedPages).find(key => pathname.includes(key));

    if (savedPathname) {
      dispatchUnpinPage(pathname);
    } else {
      dispatchPinPage({ pathname, ...recipe });
    }
  }

  render() {
    const {
      canonicalUrl,
      contentId,
      contentType,
      enableVoice,
      hash,
      previewOnly,
      shareImage,
      text,
      voiceLinks,
      pathname,
      canPinThePage,
      loading,
      pinnedPages,
    } = this.props;

    const isPinned = !!pinnedPages[pathname];
    const pageType = pathname.split('/')[1];
    const pinButtonTitle = `${isPinned ? 'Unpin' : 'Pin'} this ${pageType}`;
    const pinButtonIcon = `push-pin${isPinned ? '-filled' : ''}`;

    return (
      <div className="general-actions__row margin-bottom-20">
        {contentType === CONTENT_TYPES.RECIPE && (
          <UserRecipeFeedback contentId={contentId} hash={hash} previewOnly={previewOnly} />
        )}
        <AddToCollectionButton itemId={contentId} itemType={CONTENT_TYPES.CONTENT} />
        <Button
          color="primary"
          size="medium"
          onClick={this.bound.handlePrint}
          title="Print"
          aria-label="Print"
        >
          <Icon fill={COLOURS.ENERGIZED} name="printer" height="20" width="20" />
        </Button>
        <FavouriteButton
          className="general-actions__favourite"
          contentId={contentId}
          contentType={contentType}
          hash={hash}
        />
        <VoiceAssistantLink enabled={enableVoice} voiceLinks={voiceLinks} />
        {canPinThePage && (
          <Button
            color="primary"
            size="medium"
            className="general-actions__pin"
            onClick={this.bound.handlePinClick}
            title={pinButtonTitle}
            disabled={loading}
            aria-label={pinButtonTitle}
          >
            <Icon fill={COLOURS.ENERGIZED} name={pinButtonIcon} height="20" width="20" />
          </Button>
        )}
        <ContentOptions
          textToShare={text}
          urlToShare={canonicalUrl}
          imageToShare={shareImage}
          itemId={contentId}
          itemType={CONTENT_TYPES.CONTENT}
          contentType={contentType}
          hash={hash}
          subMenu="share"
        >
          <Icon fill={COLOURS.ENERGIZED} name="native-share" width="20" style={{ marginTop: -3 }} />
        </ContentOptions>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  baseUrl: state.config.baseUrl,
  canonicalUrl: `${state.config.baseUrl}${state.router.pathname}`,
  pathname: state.router.pathname,
  isLoggedIn: state.user.logged_in,
  nativeIOSPrinting: state.config.nativeIOSPrinting,
  tableOfContents: state.tableOfContents,
  pinnedPages: state.pinnedPages,
});

const mapDispatchToProps = {
  openTableOfContents,
  closeTOCBeforePrinting,
  pinPage,
  unpinPage,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssGeneralActions)(GeneralActions));
